import React, { useState, useEffect, useMemo, useCallback } from 'react';
import useLiveRefresh from '../../utils/useLiveRefresh';
import { useOutletContext } from 'react-router-dom';
import crmApi from '../../utils/crmApi';
import { downloadForecastWorkbook } from '../../utils/forecastWorkbook';
import { downloadForecastDeck } from '../../utils/forecastDeck';

/* ════════════════════════════════════════════════════════════════════════
   SALES FORECAST — Annual Forecast Management
   Baseline (approved & frozen) · Rolling (editable, monthly freeze) · Actual
   ════════════════════════════════════════════════════════════════════════ */

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const QUARTERS = [['Q1',[0,1,2]],['Q2',[3,4,5]],['Q3',[6,7,8]],['Q4',[9,10,11]]];
const C = { teal:'#0D9488', blue:'#2563EB', green:'#16A34A', purple:'#7C3AED', amber:'#D97706', red:'#DC2626', slate:'#64748B' };

const r3 = n => Math.round((Number(n)||0)*1000)/1000;
const num = (n) => { const v=r3(n); const f=Math.abs(v%1)>0.0005; return v.toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:f?3:0}); };
const mny = (n,cur='OMR') => `${cur} ${num(n)}`;
const uni = (n) => `${num(n)} ${Math.abs(r3(n))===1?'unit':'units'}`;
const pct = (a,b) => b!==0 ? Math.round((a-b)/b*100) : (a>0?100:0);

/* ─── tiny UI atoms ─────────────────────────────────────────────────────── */
const Badge = ({ color, bg, children }) => (
  <span style={{ fontSize:11, fontWeight:700, padding:'3px 11px', borderRadius:99, background:bg, color, whiteSpace:'nowrap' }}>{children}</span>
);
function Modal({ title, subtitle, children, onClose, width=460, footer, danger }) {
  return (
    <div className="modal-overlay" onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div className="modal" style={{ width, maxWidth:'96vw', maxHeight:'90vh', display:'flex', flexDirection:'column', padding:0 }}>
        <div style={{ padding:'18px 22px', borderBottom:'1px solid var(--border)' }}>
          <h3 style={{ fontSize:16, fontWeight:800, color:danger?C.red:'var(--text)' }}>{title}</h3>
          {subtitle && <p style={{ fontSize:12.5, color:'var(--muted)', marginTop:4, lineHeight:1.5 }}>{subtitle}</p>}
        </div>
        <div style={{ padding:'18px 22px', overflowY:'auto', flex:1 }}>{children}</div>
        {footer && <div style={{ padding:'14px 22px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', gap:8 }}>{footer}</div>}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════ */
export default function CrmForecast() {
  const { isAdmin } = useOutletContext();
  const nowY = new Date().getFullYear();

  const [year, setYear] = useState(nowY);
  const [tab, setTab] = useState('overview');
  const [groupBy, setGroupBy] = useState('category');
  const [fCustomer, setFCustomer] = useState('');
  const [fProduct, setFProduct] = useState('');
  const [colsByTab, setColsByTab] = useState({
    target:  { units: true, cost: true, sales: true, revenue: true },
    forecast:{ units: true, cost: true, sales: true, revenue: true },
    actuals: { units: true, cost: true, sales: true, revenue: true },
    salesorders: { units: true, cost: true, sales: true, revenue: true },
  });
  const cols = colsByTab[tab] || { units: true, cost: true, sales: true, revenue: true };
  const setCols = (updater) => setColsByTab(prev => ({ ...prev, [tab]: typeof updater === 'function' ? updater(prev[tab]) : updater }));

  const [forecasts, setForecasts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [actual, setActual] = useState(null);
  const [soGrid, setSoGrid] = useState(null);
  const [cats, setCats] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [pricing, setPricing] = useState({});
  const [users, setUsers] = useState([]);

  const [editing, setEditing] = useState(null);   // active baseline/rolling forecast object + values
  const [months, setMonths] = useState([]);
  const [audit, setAudit] = useState([]);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  // modal state
  const [drawer, setDrawer] = useState(null);   // { dim:'customer'|'product', id }
  const [dlg, setDlg] = useState(null);          // generic dialog descriptor

  const flash = (text, type='ok') => { setMsg({ text, type }); setTimeout(()=>setMsg(m=>m&&m.text===text?null:m), 4000); };

  /* ── data loaders ── */
  const loadList = useCallback(() => {
    crmApi.get(`/forecasts?year=${year}`).then(setForecasts).catch(()=>{});
    crmApi.get(`/forecast-analytics/${year}`).then(setAnalytics).catch(()=>setAnalytics(null));
  }, [year]);
  const loadActual = useCallback(() => crmApi.get(`/forecast-actual/${year}`).then(setActual).catch(()=>setActual(null)), [year]);
  const loadSO = useCallback(() => crmApi.get(`/forecast-sales-orders/${year}`).then(setSoGrid).catch(()=>setSoGrid(null)), [year]);

  useEffect(() => { loadList(); if (tab==='actuals') loadActual(); /* eslint-disable-next-line */ }, [year]);
  useLiveRefresh(() => { loadList(); if (tab==='actuals') loadActual(); if (tab==='salesorders') loadSO(); });
  useEffect(() => {
    crmApi.get('/categories').then(c=>setCats(Array.isArray(c)?c:[])).catch(()=>{});
    crmApi.get('/accounts').then(a=>setAccounts(Array.isArray(a)?a:[])).catch(()=>{});
    crmApi.get('/category-pricing').then(p=>setPricing(p||{})).catch(()=>{});
    crmApi.get('/users').then(u=>setUsers(Array.isArray(u)?u:[])).catch(()=>{});
  }, []);

  /* ── lookups ── */
  const catName  = id => (cats.find(c=>c.id===id)||{}).name || 'Product';
  const acctName = id => id ? ((accounts.find(a=>a.id===id)||{}).name || 'Customer') : 'Unassigned';
  const priceOf  = id => pricing[id] || { price:0, cost:0, margin:0, currency:'OMR' };
  const curOf    = id => priceOf(id).currency || 'OMR';

  const baselines = forecasts.filter(f=>f.forecast_type==='target');
  const activeBaseline = baselines.find(f=>f.is_active) || baselines.find(f=>f.status==='approved') || baselines[0];
  const rolling = forecasts.find(f=>f.forecast_type==='forecast');

  /* ── open a forecast for editing/viewing ── */
  const openForecast = useCallback(async (id, asTab) => {
    setBusy(true);
    try {
      const f = await crmApi.get(`/forecasts/${id}`);
      const cells = {}; const lines = [];
      (f.values||[]).forEach(v => {
        cells[`${v.category_id}|${v.account_id||''}|${v.month}`] = v;
        if (!lines.find(l=>l.category_id===v.category_id && l.account_id===(v.account_id||'')))
          lines.push({ category_id:v.category_id, account_id:v.account_id||'' });
      });
      setEditing({ ...f, cells, lines });
      setTab(asTab);
      if (asTab==='forecast') {
        crmApi.get(`/forecasts/${id}/months`).then(setMonths).catch(()=>setMonths([]));
        crmApi.get(`/forecasts/${id}/audit`).then(setAudit).catch(()=>setAudit([]));
      }
    } catch(e){ flash(e?.error||'Could not open forecast','error'); }
    setBusy(false);
  }, []);

  const ensureAndOpen = useCallback(async (type) => {
    setMsg(null);
    let f = type==='target' ? activeBaseline : rolling;
    if (!f) {
      try { const res = await crmApi.post('/forecasts',{forecast_type:type,year}); f={id:res.id}; }
      catch(e){ flash(e?.error||'Could not create','error'); return; }
    }
    await openForecast(f.id, type);
    loadList();
  }, [activeBaseline, rolling, year, openForecast, loadList]);

  function selectTab(k) {
    setMsg(null);
    if (k==='target'||k==='forecast') ensureAndOpen(k);
    else { setEditing(null); setTab(k); loadList(); if (k==='actuals') loadActual(); if (k==='salesorders') loadSO(); }
  }

  /* ── cell math ── */
  const cellMetrics = useCallback((cells, cat, acct, m) => {
    const v = cells[`${cat}|${acct||''}|${m}`]; const u = v ? (Number(v.amount)||0) : 0;
    if (v && v.frozen && v.snap_revenue!=null)
      return { units:u, cost:r3(u*Number(v.snap_cost||0)), sales:r3(u*Number(v.snap_price||0)), rev:Number(v.snap_revenue) };
    const p = priceOf(cat);
    return { units:u, cost:r3(u*p.cost), sales:r3(u*p.price), rev:r3(u*(p.margin!=null?p.margin:p.price-p.cost)) };
  }, [pricing]);

  /* ── persist a single cell ── */
  const saveCell = useCallback(async (catId, acctId, month, units, reason) => {
    if (!editing) return;
    try {
      await crmApi.put(`/forecasts/${editing.id}/value`, { category_id:catId, account_id:acctId||null, month, amount:parseFloat(units)||0, reason });
      setEditing(prev => {
        if (!prev) return prev;
        const key=`${catId}|${acctId||''}|${month}`;
        const cells={...prev.cells, [key]:{...(prev.cells[key]||{category_id:catId,account_id:acctId||'',month}), amount:parseFloat(units)||0}};
        const lines = prev.lines.find(l=>l.category_id===catId&&l.account_id===(acctId||'')) ? prev.lines : [...prev.lines,{category_id:catId,account_id:acctId||''}];
        return {...prev, cells, lines};
      });
      if (editing.forecast_type==='forecast') crmApi.get(`/forecasts/${editing.id}/audit`).then(setAudit).catch(()=>{});
      loadList();
    } catch(e){ flash(e?.error||'Could not save','error'); }
  }, [editing, loadList]);

  /* ── actions ── */
  const act = async (fn, okMsg) => { setBusy(true); try { await fn(); if(okMsg) flash(okMsg); } catch(e){ flash(e?.error||'Action failed','error'); } setBusy(false); };
  const submitBaseline = (approverId) => act(async()=>{ await crmApi.post(`/forecasts/${editing.id}/submit`,{approver_id:approverId}); setDlg(null); setEditing(null); loadList(); }, 'Baseline submitted for approval.');
  const approveBaseline = (f) => act(async()=>{ await crmApi.post(`/forecasts/${f.id}/approve`); setEditing(null); loadList(); }, 'Baseline approved & frozen.');
  const setActive = (f) => act(async()=>{ await crmApi.post(`/forecasts/${f.id}/set-active`); await openForecast(f.id,'target'); loadList(); }, 'Active baseline updated.');
  const reseed = () => act(async()=>{ const r=await crmApi.post(`/forecasts/${editing.id}/reseed`); await openForecast(editing.id,'forecast'); flash(`Rolling refreshed from baseline (${r.copied} lines).`); });
  const newDraft = (name) => act(async()=>{ const r=await crmApi.post('/forecasts',{forecast_type:'target',year,name}); setDlg(null); loadList(); await openForecast(r.id,'target'); });
  const delForecast = (f) => act(async()=>{ await crmApi.delete(`/forecasts/${f.id}`); setDlg(null); setEditing(null); setTab('overview'); loadList(); }, 'Deleted.');
  const resetYear = () => act(async()=>{ await crmApi.post(`/forecast-reset/${year}`); setDlg(null); setEditing(null); setTab('overview'); loadList(); }, `All ${year} forecast data was reset.`);
  const submitMonth = (m, approverId) => act(async()=>{ await crmApi.post(`/forecasts/${editing.id}/months/${m}/submit`,{approver_id:approverId}); setDlg(null); crmApi.get(`/forecasts/${editing.id}/months`).then(setMonths); }, 'Month submitted for freeze.');
  const approveMonth = (m) => act(async()=>{ await crmApi.post(`/forecasts/${editing.id}/months/${m}/approve`); await openForecast(editing.id,'forecast'); }, 'Month frozen.');
  const unfreezeMonth = (m) => act(async()=>{ await crmApi.post(`/forecasts/${editing.id}/months/${m}/unfreeze`); await openForecast(editing.id,'forecast'); }, `${MONTHS[m-1]} re-opened.`);

  const years = Array.from({ length: (2035 - 2022) + 1 }, (_, i) => 2022 + i);
  const TABS = [['overview','Overview'],['target','Baseline'],['forecast','Rolling'],['actuals','Actual Sales'],['salesorders','Service Orders']];

  return (
    <div style={{ maxWidth:1700, margin:'0 auto' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:16, flexWrap:'wrap', marginBottom:18 }}>
        <div style={{ flex:1, minWidth:240 }}>
          <h1 style={{ fontSize:21, fontWeight:800, letterSpacing:'-0.02em' }}>Sales Forecast</h1>
          <p style={{ fontSize:13, color:'var(--muted)', marginTop:2 }}>Annual forecast management — baseline plan, rolling working forecast, and verified actual performance.</p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button className="btn btn-sm" onClick={()=>act(()=>downloadForecastWorkbook(year),'Excel report downloaded.')}>⤓ Excel report</button>
          <button className="btn btn-sm" onClick={()=>act(()=>downloadForecastDeck(year),'PowerPoint report downloaded.')}>⤓ PowerPoint</button>
          {isAdmin && <button className="btn btn-sm" style={{ color:C.red, borderColor:'rgba(220,38,38,0.3)' }} onClick={()=>setDlg({kind:'resetYear'})}>Reset year</button>}
          <select className="select" style={{ width:96 }} value={year} onChange={e=>{ setYear(parseInt(e.target.value)); setEditing(null); setTab('overview'); }}>
            {years.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display:'flex', gap:4, marginBottom:18, background:'var(--surface2)', padding:4, borderRadius:12, width:'fit-content', maxWidth:'100%', flexWrap:'wrap' }}>
        {TABS.map(([k,label])=>(
          <button key={k} onClick={()=>selectTab(k)} style={{
            fontSize:13, fontWeight:700, padding:'8px 18px', borderRadius:9, cursor:'pointer', border:'none',
            background: tab===k?'var(--surface)':'transparent', color: tab===k?C.teal:'var(--muted2)',
            boxShadow: tab===k?'0 1px 3px rgba(0,0,0,0.08)':'none' }}>{label}</button>
        ))}
      </div>

      {msg && <div style={{ marginBottom:14, padding:'11px 16px', borderRadius:10, fontSize:13, fontWeight:600,
        background: msg.type==='error'?'var(--red-dim)':'rgba(13,148,136,0.1)', color: msg.type==='error'?C.red:C.teal }}>{msg.text}</div>}

      {/* Toolbar for grid tabs */}
      {tab!=='overview' && (
        <Toolbar groupBy={groupBy} setGroupBy={setGroupBy} fCustomer={fCustomer} setFCustomer={setFCustomer}
          fProduct={fProduct} setFProduct={setFProduct} accounts={accounts} cats={cats}
          cols={cols} setCols={setCols} isAdmin={isAdmin} />
      )}

      {/* Panels */}
      {tab==='overview' && <Overview analytics={analytics} year={year} onBuild={()=>selectTab('target')} />}

      {(tab==='target'||tab==='forecast') && editing && (
        <ForecastGrid
          mode={tab} editing={editing} isAdmin={isAdmin} busy={busy}
          groupBy={groupBy} fCustomer={fCustomer} fProduct={fProduct}
          catName={catName} acctName={acctName} curOf={curOf} cellMetrics={cellMetrics}
          baselines={baselines} openForecast={openForecast} cols={cols}
          onOpenDrawer={(dim,id)=>setDrawer({dim,id})}
          onDialog={setDlg} onApproveBaseline={approveBaseline} onSetActive={setActive} onReseed={reseed}
          months={months} onSubmitMonth={(m)=>setDlg({kind:'freezeMonth',month:m})} onApproveMonth={approveMonth} onUnfreezeMonth={unfreezeMonth}
          audit={audit} year={year}
        />
      )}

      {tab==='actuals' && (
        <ActualGrid A={actual} groupBy={groupBy} fCustomer={fCustomer} fProduct={fProduct} cols={cols}
          catName={catName} acctName={acctName} priceOf={priceOf} />
      )}

      {tab==='salesorders' && (
        <SalesOrderGrid data={soGrid} groupBy={groupBy} fCustomer={fCustomer} fProduct={fProduct} cols={cols}
          catName={catName} acctName={acctName} curOf={curOf} cellMetrics={cellMetrics} />
      )}

      {/* ── Customer/Product editor drawer ── */}
      {drawer && editing && (
        <EditorDrawer
          drawer={drawer} onClose={()=>setDrawer(null)}
          editing={editing} mode={tab} cats={cats} accounts={accounts}
          catName={catName} acctName={acctName} curOf={curOf} priceOf={priceOf}
          cellMetrics={cellMetrics} saveCell={saveCell}
        />
      )}

      {/* ── Dialogs ── */}
      {dlg && (
        <Dialogs dlg={dlg} onClose={()=>setDlg(null)} users={users} year={year} editing={editing}
          onNewDraft={newDraft} onDeleteForecast={delForecast} onResetYear={resetYear}
          onSubmitBaseline={submitBaseline} onSubmitMonth={submitMonth} busy={busy} />
      )}
    </div>
  );
}

/* ─── Toolbar ────────────────────────────────────────────────────────────── */
function Toolbar({ groupBy, setGroupBy, fCustomer, setFCustomer, fProduct, setFProduct, accounts, cats, cols, setCols, isAdmin }) {
  const active = fCustomer||fProduct;
  const [open, setOpen] = useState(false);
  const COLDEF = [['units','Units / Count'],['cost','Cost'],['sales','Sales price'],['revenue','Revenue (margin)']];
  return (
    <div style={{ display:'flex', gap:14, alignItems:'center', marginBottom:14, flexWrap:'wrap',
      background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'10px 14px' }}>
      <Field label="View">
        <select className="select" style={{ width:200 }} value={groupBy} onChange={e=>setGroupBy(e.target.value)}>
          <option value="category">By product → customer</option>
          <option value="customer">By customer → product</option>
        </select>
      </Field>
      <Field label="Customer">
        <select className="select" style={{ width:180 }} value={fCustomer} onChange={e=>setFCustomer(e.target.value)}>
          <option value="">All customers</option>
          {accounts.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </Field>
      <Field label="Product">
        <select className="select" style={{ width:180 }} value={fProduct} onChange={e=>setFProduct(e.target.value)}>
          <option value="">All products</option>
          {cats.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      {cols && setCols && (
        <Field label="Columns">
          <div style={{ position:'relative' }}>
            <button className="btn" style={{ width:200, textAlign:'left', display:'flex', justifyContent:'space-between', alignItems:'center' }} onClick={()=>setOpen(o=>!o)}>
              <span>{COLDEF.filter(([k])=>cols[k]).length} of 4 shown</span><span style={{ color:'var(--muted)' }}>▾</span>
            </button>
            {open && (
              <div style={{ position:'absolute', zIndex:30, top:'calc(100% + 4px)', left:0, width:220, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', padding:8 }} onMouseLeave={()=>setOpen(false)}>
                <div style={{ fontSize:10.5, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', padding:'4px 8px' }}>Show in grid</div>
                {COLDEF.map(([k,label])=>(
                  <label key={k} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 8px', fontSize:13, cursor:'pointer', borderRadius:6 }}>
                    <input type="checkbox" checked={!!cols[k]} onChange={e=>setCols(c=>({ ...c, [k]: e.target.checked }))} />
                    {label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </Field>
      )}
      {active && <button className="btn btn-sm" style={{ alignSelf:'flex-end' }} onClick={()=>{ setFCustomer(''); setFProduct(''); }}>Clear</button>}
    </div>
  );
}
const Field = ({ label, children }) => (
  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
    <span style={{ fontSize:10.5, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px' }}>{label}</span>
    {children}
  </div>
);

/* ─── Overview ───────────────────────────────────────────────────────────── */
function Overview({ analytics, year, onBuild }) {
  if (!analytics) return <Empty>Loading overview…</Empty>;
  if (analytics.error_debug) return <div className="card" style={{ padding:'20px', fontSize:13, color:C.red }}>Overview could not be calculated: {analytics.error_debug}. Please send this message to support.</div>;
  const t=analytics.totals; const v=analytics.variance;
  const empty = !analytics.has_target && !analytics.has_forecast && t.target===0 && t.forecast===0 && t.performance===0;
  if (empty) return (
    <div className="card" style={{ padding:'48px 24px', textAlign:'center' }}>
      <div style={{ fontSize:15, fontWeight:700, marginBottom:6 }}>No forecast data for {year} yet</div>
      <p style={{ fontSize:13, color:'var(--muted)', marginBottom:18 }}>Create the Baseline Forecast and enter monthly units to see performance here.</p>
      <button className="btn btn-primary" onClick={onBuild}>Build Baseline Forecast</button>
    </div>
  );
  const Kpi = ({ label, value, color, sub }) => (
    <div className="card" style={{ padding:18 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px' }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:800, marginTop:6, color }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'var(--muted)', marginTop:3 }}>{sub}</div>}
    </div>
  );
  const Var = ({ label, val }) => (
    <div className="card" style={{ padding:18 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px' }}>{label}</div>
      <div style={{ fontSize:20, fontWeight:800, marginTop:6, color: val>=0?C.green:C.red }}>{val>=0?'+':''}{mny(val)}</div>
    </div>
  );
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:14 }}>
        <Kpi label="Baseline Revenue" value={mny(t.target)} color={C.blue} sub={analytics.target_name||'approved plan'} />
        <Kpi label="Rolling Revenue" value={mny(t.forecast)} color={C.purple} sub="working forecast" />
        <Kpi label="Actual Revenue" value={mny(t.performance)} color={C.green} sub="verified from frozen months" />
        <Kpi label="Forecast Accuracy" value={`${analytics.accuracy_pct}%`} color={C.teal} sub="actual vs baseline" />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:14 }}>
        <Var label="Rolling vs Baseline" val={v.forecast_vs_target} />
        <Var label="Actual vs Baseline" val={v.perf_vs_target} />
        <Var label="Actual vs Rolling" val={v.perf_vs_forecast} />
      </div>
      <div className="card" style={{ padding:22 }}>
        <div style={{ fontSize:14, fontWeight:800, marginBottom:4 }}>Monthly comparison — {year}</div>
        <div style={{ display:'flex', gap:18, fontSize:11, color:'var(--muted)', marginBottom:18 }}>
          <Leg c={C.blue} t="Baseline"/><Leg c={C.purple} t="Rolling"/><Leg c={C.green} t="Actual"/>
        </div>
        <Chart months={analytics.months}/>
      </div>
    </div>
  );
}
const Leg = ({c,t}) => <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:3, background:c }}/>{t}</span>;
function Chart({ months }) {
  const max = Math.max(1, ...months.flatMap(m=>[m.target,m.forecast,m.performance]));
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:210 }}>
      {months.map((m,i)=>(
        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', height:'100%' }}>
          <div style={{ flex:1, display:'flex', alignItems:'flex-end', gap:2, width:'100%', justifyContent:'center' }}>
            {[['target',C.blue],['forecast',C.purple],['performance',C.green]].map(([k,c])=>(
              <div key={k} title={`${k}: ${mny(m[k])}`} style={{ width:'28%', height:`${(m[k]/max)*100}%`, background:c, borderRadius:'3px 3px 0 0', minHeight:m[k]?2:0, transition:'height .3s' }}/>
            ))}
          </div>
          <div style={{ fontSize:9, color:'var(--muted)', marginTop:6 }}>{MONTHS[i]}</div>
        </div>
      ))}
    </div>
  );
}

/* ─── Forecast grid (Baseline / Rolling) — display only, edit via drawer ─── */
function ForecastGrid(props) {
  const { mode, editing, isAdmin, busy, groupBy, fCustomer, fProduct, catName, acctName, curOf, cellMetrics,
    baselines, openForecast, cols={units:true,cost:true,sales:true,revenue:true}, onOpenDrawer, onDialog, onApproveBaseline, onSetActive, onReseed,
    months, onSubmitMonth, onApproveMonth, onUnfreezeMonth, audit, year } = props;
  const locked = !!editing.locked;
  const cells = editing.cells;

  const lines = useMemo(() => editing.lines.filter(l =>
    l.category_id && l.account_id &&
    (!fCustomer || l.account_id===fCustomer) && (!fProduct || l.category_id===fProduct)
  ), [editing.lines, fCustomer, fProduct]);

  const groups = useMemo(() => {
    const g={};
    lines.forEach(l=>{ const p=groupBy==='category'?l.category_id:(l.account_id||''); const s=groupBy==='category'?(l.account_id||''):l.category_id; g[p]=g[p]||[]; if(!g[p].includes(s)) g[p].push(s); });
    return g;
  }, [lines, groupBy]);
  const lc=(p,s)=>groupBy==='category'?p:s, la=(p,s)=>groupBy==='category'?s:p;

  const monthMoney = (secs,p,m) => secs.reduce((a,s)=>{ const x=cellMetrics(cells,lc(p,s),la(p,s),m); a.units+=x.units;a.cost+=x.cost;a.sales+=x.sales;a.rev+=x.rev; return a; },{units:0,cost:0,sales:0,rev:0});
  const lineTotal = (cat,acct) => MONTHS.reduce((a,_,i)=>{ const x=cellMetrics(cells,cat,acct,i+1); a.units+=x.units;a.cost+=x.cost;a.sales+=x.sales;a.rev+=x.rev; return a; },{units:0,cost:0,sales:0,rev:0});
  const grandMonth = (m) => lines.reduce((a,l)=>{ const x=cellMetrics(cells,l.category_id,l.account_id,m); a.cost+=x.cost;a.sales+=x.sales;a.rev+=x.rev; return a; },{cost:0,sales:0,rev:0});

  const Money3 = ({ cost, sales, rev, bold }) => (cost||sales||rev) ? (
    <div style={{ lineHeight:1.4 }}>
      {cols.cost && <div style={{ color:C.amber }}>{num(cost)}</div>}
      {cols.sales && <div style={{ color:C.blue }}>{num(sales)}</div>}
      {cols.revenue && <div style={{ color:C.green, fontWeight:bold?800:700 }}>{num(rev)}</div>}
    </div>
  ) : <span style={{ color:'var(--muted)' }}>–</span>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      {/* Status bar */}
      <div className="card" style={{ padding:'14px 18px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <div style={{ fontSize:14, fontWeight:800 }}>{mode==='target'?'Baseline Forecast':'Rolling Forecast'} · {year}</div>
        {locked ? <Badge color={C.green} bg="rgba(22,163,74,0.12)">Approved &amp; Frozen</Badge>
          : editing.status==='pending_approval' ? <Badge color={C.amber} bg="rgba(217,119,6,0.12)">Pending Approval</Badge>
          : <Badge color={C.slate} bg="var(--surface2)">Draft</Badge>}
        {mode==='target' && editing.is_active && <Badge color={C.teal} bg="rgba(13,148,136,0.15)">★ Active plan</Badge>}
        {mode==='target' && baselines.length>1 && (
          <select className="select" style={{ width:'auto', fontSize:12, padding:'5px 8px' }} value={editing.id} onChange={e=>openForecast(e.target.value,'target')}>
            {baselines.map(b=><option key={b.id} value={b.id}>{(b.name||(b.status==='approved'?'Approved':'Draft'))}{b.is_active?' ★':''} · {b.status}</option>)}
          </select>
        )}
        <div style={{ marginLeft:'auto', display:'flex', gap:8, flexWrap:'wrap' }}>
          {!locked && <button className="btn btn-sm btn-primary" disabled={busy} onClick={()=>onOpenDrawer('customer','')}>+ Add line</button>}
          {mode==='target' && isAdmin && <button className="btn btn-sm" disabled={busy} onClick={()=>onDialog({kind:'newDraft'})}>New draft</button>}
          {mode==='forecast' && isAdmin && <button className="btn btn-sm" disabled={busy} onClick={onReseed}>↻ Reseed from baseline</button>}
          {mode==='target' && isAdmin && !locked && editing.status==='draft' && <button className="btn btn-sm btn-primary" disabled={busy} onClick={()=>onDialog({kind:'submitBaseline'})}>Submit for approval</button>}
          {mode==='target' && editing.status==='pending_approval' && editing.approver_id && <button className="btn btn-sm" style={{ background:C.green, color:'#fff' }} disabled={busy} onClick={()=>onApproveBaseline(editing)}>Approve &amp; freeze</button>}
          {mode==='target' && isAdmin && editing.status==='approved' && !editing.is_active && <button className="btn btn-sm" style={{ background:C.teal, color:'#fff' }} disabled={busy} onClick={()=>onSetActive(editing)}>Make active</button>}
          {mode==='target' && isAdmin && <button className="btn btn-sm" style={{ color:C.red }} disabled={busy} onClick={()=>onDialog({kind:'deleteForecast',forecast:editing})}>Delete {editing.status==='approved'?'baseline':'draft'}</button>}
        </div>
      </div>

      <div style={{ fontSize:12, color:'var(--muted)', display:'flex', gap:16, flexWrap:'wrap', alignItems:'center' }}>
        <span>Click a heading or line to edit in a panel.</span>
        <span style={{ display:'flex', gap:12 }}>
          <Leg c={C.amber} t="Cost"/><Leg c={C.blue} t="Sales"/><Leg c={C.green} t="Revenue"/>
        </span>
        <span style={{ color:'var(--muted)' }}>Customer totals sum money only — units shown per product.</span>
      </div>

      {/* Grid */}
      <div className="card" style={{ padding:0, overflowX:'auto' }}>
        {lines.length===0 ? <Empty>{(fCustomer||fProduct)?'No lines match the filter.':'No forecast lines yet. Use “+ Add line”.'}</Empty> : (
          <table style={{ borderCollapse:'collapse', width:'100%', fontSize:12, minWidth:1340, tableLayout:'fixed' }}>
            <thead>
              <tr style={{ background:'var(--surface2)' }}>
                <th style={{ ...thL, width:200 }}>{groupBy==='category'?'Product / Customer':'Customer / Product'}</th>
                {MONTHS.map(m=><th key={m} style={{ ...thC, width:90 }}>{m}</th>)}
                <th style={{ ...thR, width:130 }}>Year Total</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groups).map(([pid,secs])=>{
                const pName = groupBy==='category'?catName(pid):acctName(pid);
                const gTot = MONTHS.reduce((a,_,i)=>{ const x=monthMoney(secs,pid,i+1); a.units+=x.units;a.cost+=x.cost;a.sales+=x.sales;a.rev+=x.rev; return a; },{units:0,cost:0,sales:0,rev:0});
                const showUnits = groupBy==='category' && cols.units; // units sum only valid per product
                return (
                  <React.Fragment key={pid}>
                    <tr style={{ background:'var(--surface2)', borderTop:'2px solid var(--border)', cursor:'pointer' }}
                      onClick={()=>onOpenDrawer(groupBy==='customer'?'customer':'product', pid)}>
                      <td style={{ ...tdL, fontWeight:800, color:C.teal, background:'var(--surface2)' }}>{pName} <span style={{ fontSize:11 }}>✎</span></td>
                      {MONTHS.map((_,i)=>{ const mm=monthMoney(secs,pid,i+1); return (
                        <td key={i} style={tdC10}>{(mm.units||mm.cost||mm.sales||mm.rev)?(
                          <div style={{ lineHeight:1.4 }}>
                            {showUnits && <div style={{ fontWeight:800 }}>{num(mm.units)}</div>}
                            {cols.cost && <div style={{ color:C.amber }}>{num(mm.cost)}</div>}
                            {cols.sales && <div style={{ color:C.blue }}>{num(mm.sales)}</div>}
                            {cols.revenue && <div style={{ color:C.green, fontWeight:800 }}>{num(mm.rev)}</div>}
                          </div>
                        ):<span style={{ color:'var(--muted)' }}>–</span>}</td>
                      ); })}
                      <td style={{ ...tdR, fontSize:10, lineHeight:1.5 }}>
                        {showUnits && <div style={{ fontWeight:800 }}>{uni(gTot.units)}</div>}
                        {cols.cost && <div style={{ color:C.amber }}>{num(gTot.cost)}</div>}
                        {cols.sales && <div style={{ color:C.blue }}>{num(gTot.sales)}</div>}
                        {cols.revenue && <div style={{ color:C.green, fontWeight:800 }}>{num(gTot.rev)}</div>}
                      </td>
                    </tr>
                    {secs.map(s=>{
                      const cat=lc(pid,s), acct=la(pid,s);
                      const sName = groupBy==='category'?acctName(s):catName(s);
                      const lt = lineTotal(cat,acct);
                      return (
                        <tr key={s} style={{ borderTop:'1px solid var(--border)', cursor:'pointer' }}
                          onClick={()=>onOpenDrawer(groupBy==='customer'?'product':'customer', s)}>
                          <td style={{ ...tdL, paddingLeft:26, color:'var(--muted2)' }}>{sName}</td>
                          {MONTHS.map((_,i)=>{ const x=cellMetrics(cells,cat,acct,i+1); return (
                            <td key={i} style={tdC10}>{x.units>0
                              ? <div style={{ lineHeight:1.4 }}>
                                  {cols.units && <div style={{ fontWeight:700 }}>{num(x.units)}</div>}
                                  {cols.cost && <div style={{ color:C.amber }}>{num(x.cost)}</div>}
                                  {cols.sales && <div style={{ color:C.blue }}>{num(x.sales)}</div>}
                                  {cols.revenue && <div style={{ color:C.green, fontWeight:700 }}>{num(x.rev)}</div>}
                                </div>
                              : <span style={{ color:'var(--muted)' }}>–</span>}</td>
                          ); })}
                          <td style={{ ...tdR, fontSize:10, lineHeight:1.5 }}>
                            {cols.units && <div style={{ fontWeight:700 }}>{uni(lt.units)}</div>}
                            {cols.cost && <div style={{ color:C.amber }}>Cost {num(lt.cost)}</div>}
                            {cols.sales && <div style={{ color:C.blue }}>Sales {num(lt.sales)}</div>}
                            {cols.revenue && <div style={{ color:C.green }}>{mny(lt.rev, curOf(cat))}</div>}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              <tr style={{ borderTop:'2px solid var(--border)', background:'var(--surface2)' }}>
                <td style={{ ...tdL, fontWeight:800, background:'var(--surface2)' }}>Grand Total</td>
                {MONTHS.map((_,i)=><td key={i} style={tdC10}><Money3 {...grandMonth(i+1)} bold/></td>)}
                <td style={{ ...tdR, fontSize:10, lineHeight:1.5 }}>
                  {(()=>{ const g=MONTHS.reduce((a,_,i)=>{ const x=grandMonth(i+1); a.cost+=x.cost;a.sales+=x.sales;a.rev+=x.rev; return a; },{cost:0,sales:0,rev:0}); return (
                    <>{cols.cost && <div style={{ color:C.amber }}>{num(g.cost)}</div>}{cols.sales && <div style={{ color:C.blue }}>{num(g.sales)}</div>}{cols.revenue && <div style={{ color:C.green, fontWeight:800 }}>{num(g.rev)}</div>}</>
                  ); })()}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {locked && <div style={{ fontSize:12, color:'var(--muted)' }}>This baseline is approved and frozen — later price or catalog changes will not alter it. Revise via the Rolling Forecast.</div>}

      {/* Rolling-only: monthly freeze + audit */}
      {mode==='forecast' && (
        <div className="card" style={{ padding:'18px 20px' }}>
          <div style={{ fontSize:14, fontWeight:800, marginBottom:4 }}>Monthly Freeze</div>
          <p style={{ fontSize:12, color:'var(--muted)', marginBottom:14 }}>Lock a month's values permanently once finalized. Frozen months feed Actual Sales and never change with later pricing.</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(116px,1fr))', gap:8 }}>
            {months.map(m=>{
              const st=m.status||'open';
              const meta = st==='frozen'?{c:C.green,l:'Frozen'}:st==='pending_approval'?{c:C.amber,l:'Pending'}:{c:'var(--muted)',l:'Open'};
              return (
                <div key={m.month} style={{ border:'1px solid var(--border)', borderRadius:9, padding:'9px 11px' }}>
                  <div style={{ fontSize:12, fontWeight:700 }}>{MONTHS[m.month-1]}</div>
                  <div style={{ fontSize:11, color:meta.c, fontWeight:600, margin:'2px 0 7px' }}>{st==='frozen'?'🔒 ':''}{meta.l}</div>
                  {st==='open' && <button className="btn btn-sm" style={{ width:'100%' }} disabled={busy} onClick={()=>onSubmitMonth(m.month)}>Submit</button>}
                  {st==='pending_approval' && <button className="btn btn-sm" style={{ width:'100%', background:C.green, color:'#fff' }} disabled={busy} onClick={()=>onApproveMonth(m.month)}>Approve</button>}
                  {st==='frozen' && isAdmin && <button className="btn btn-sm" style={{ width:'100%' }} disabled={busy} onClick={()=>onUnfreezeMonth(m.month)}>Re-open</button>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {mode==='forecast' && (
        <div className="card" style={{ padding:'18px 20px' }}>
          <div style={{ fontSize:14, fontWeight:800, marginBottom:12 }}>Revision History</div>
          {audit.length===0 ? <div style={{ fontSize:12, color:'var(--muted)' }}>No changes recorded yet.</div> :
            audit.slice(0,50).map(a=>(
              <div key={a.id} style={{ display:'flex', gap:10, padding:'9px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
                <div style={{ flex:1 }}>
                  <strong>{catName(a.category_id)}</strong> · {MONTHS[(a.month||1)-1]} {year} — <span style={{ color:'var(--muted)' }}>{uni(a.old_value)}</span> → <strong>{uni(a.new_value)}</strong>
                  {a.reason && <div style={{ color:'var(--muted2)', marginTop:2 }}>“{a.reason}”</div>}
                </div>
                <div style={{ fontSize:11, color:'var(--muted)', textAlign:'right', whiteSpace:'nowrap' }}>{a.user_name}<br/>{new Date(a.created_at).toLocaleDateString()}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

/* ─── Editor drawer: works for a CUSTOMER (lists products) or a PRODUCT (lists customers) ── */
function EditorDrawer({ drawer, onClose, editing, mode, cats, accounts, catName, acctName, curOf, priceOf, cellMetrics, saveCell }) {
  const locked = !!editing.locked;
  const cells = editing.cells;
  const byCustomer = drawer.dim === 'customer';           // anchor dimension
  const anchorOptions = byCustomer ? accounts : cats;
  const partnerOptions = byCustomer ? cats : accounts;

  const [anchorId, setAnchorId] = useState(drawer.id || '');

  // partner ids already present for this anchor (existing lines)
  const existingPartners = () => editing.lines
    .filter(l => byCustomer ? l.account_id === anchorId : l.category_id === anchorId)
    .map(l => byCustomer ? l.category_id : l.account_id)
    .filter(Boolean);
  const [rows, setRows] = useState([]);
  useEffect(() => { setRows([...new Set(existingPartners())]); }, [anchorId]); // eslint-disable-line

  const [addId, setAddId] = useState('');
  const addable = partnerOptions.filter(o => !rows.includes(o.id));

  // resolve (category, account) for a given partner id
  const pair = (partnerId) => byCustomer ? { cat: partnerId, acct: anchorId } : { cat: anchorId, acct: partnerId };
  const unitsAt = (partnerId, m) => { const { cat, acct } = pair(partnerId); const v = cells[`${cat}|${acct || ''}|${m}`]; return v ? (Number(v.amount) || 0) : 0; };

  const anchorName = anchorId ? (byCustomer ? acctName(anchorId) : catName(anchorId)) : '';
  const partnerName = (id) => byCustomer ? catName(id) : acctName(id);

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ justifyContent: 'flex-end', alignItems: 'stretch', padding: 0 }}>
      <div style={{ width: 660, maxWidth: '100vw', background: 'var(--surface)', height: '100vh', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 32px rgba(0,0,0,0.18)' }}>

        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>{mode === 'target' ? 'Baseline' : 'Rolling'} · edit {byCustomer ? 'customer' : 'product'}</div>
            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>{anchorName || `Choose a ${byCustomer ? 'customer' : 'product'}`}</div>
          </div>
          <button className="btn btn-sm" onClick={onClose}>Close</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '18px 22px' }}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>1 · {byCustomer ? 'Customer' : 'Product'}</div>
            <select className="select" style={{ width: '100%' }} value={anchorId} onChange={e => setAnchorId(e.target.value)} autoFocus={!anchorId}>
              <option value="">Select a {byCustomer ? 'customer' : 'product'}…</option>
              {anchorOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>

          {!anchorId ? (
            <Empty>Select a {byCustomer ? 'customer' : 'product'} to begin.</Empty>
          ) : (
            <>
              {!locked && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>2 · Add a {byCustomer ? 'product' : 'customer'}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select className="select" style={{ flex: 1 }} value={addId} onChange={e => setAddId(e.target.value)}>
                      <option value="">Select a {byCustomer ? 'product' : 'customer'}…</option>
                      {addable.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    <button className="btn btn-primary" disabled={!addId} onClick={() => { setRows(r => [...r, addId]); setAddId(''); }}>Add</button>
                  </div>
                </div>
              )}

              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                3 · {byCustomer ? 'Products & monthly quantities' : 'Customers & monthly quantities'}
              </div>
              {rows.length === 0 ? <Empty>No {byCustomer ? 'products' : 'customers'} yet.{locked ? '' : ` Add one above.`}</Empty> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {rows.map(partnerId => {
                    const { cat, acct } = pair(partnerId);
                    const p = priceOf(cat), cur = curOf(cat);
                    const tot = MONTHS.reduce((a, _, i) => { const x = cellMetrics(cells, cat, acct, i + 1); a.units += x.units; a.rev += x.rev; return a; }, { units: 0, rev: 0 });
                    return (
                      <div key={partnerId} style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 800 }}>{partnerName(partnerId)}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Sell {mny(p.price, cur)} · Cost {mny(p.cost, cur)} · Margin {mny(p.margin != null ? p.margin : p.price - p.cost, cur)} / unit</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Year</div>
                            <div style={{ fontSize: 13, fontWeight: 800, color: C.green }}>{uni(tot.units)} · {mny(tot.rev, cur)}</div>
                          </div>
                        </div>
                        <div style={{ padding: '14px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                          {QUARTERS.map(([ql, idxs]) => (
                            <div key={ql}>
                              <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>{ql}</div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                {idxs.map(i => (
                                  <label key={i} style={{ flex: 1 }}>
                                    <div style={{ fontSize: 10, color: 'var(--muted2)', marginBottom: 3, textAlign: 'center' }}>{MONTHS[i]}</div>
                                    <input type="number" min="0" placeholder="0" disabled={locked} defaultValue={unitsAt(partnerId, i + 1) || ''}
                                      onBlur={e => { const v = e.target.value; if ((Number(v) || 0) !== unitsAt(partnerId, i + 1)) saveCell(cat, acct, i + 1, v, ''); }}
                                      style={{ width: '100%', padding: '8px 4px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 7, textAlign: 'center', background: locked ? 'var(--surface2)' : 'var(--surface)', color: 'var(--text)' }} />
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Service Orders grid (read-only, derived from approved service orders) ─── */
function SalesOrderGrid({ data, groupBy, fCustomer, fProduct, cols={units:true,cost:true,sales:true,revenue:true}, catName, acctName, curOf, cellMetrics }) {
  if (!data) return <Empty>Loading service orders…</Empty>;
  const cells = {};
  const lines = [];
  (data.values||[]).forEach(v => {
    if (!v.category_id || !v.account_id) return;
    cells[`${v.category_id}|${v.account_id}|${v.month}`] = v;
    if (!lines.find(l=>l.category_id===v.category_id && l.account_id===v.account_id)) lines.push({ category_id:v.category_id, account_id:v.account_id });
  });
  const ls = lines.filter(l => (!fCustomer||l.account_id===fCustomer) && (!fProduct||l.category_id===fProduct));
  const groups = {}; ls.forEach(l=>{ const p=groupBy==='category'?l.category_id:l.account_id; const s=groupBy==='category'?l.account_id:l.category_id; groups[p]=groups[p]||[]; if(!groups[p].includes(s)) groups[p].push(s); });
  const lc=(p,s)=>groupBy==='category'?p:s, la=(p,s)=>groupBy==='category'?s:p;
  const cm = (cat,acct,m) => cellMetrics(cells, cat, acct, m);
  const monthMoney=(secs,p,m)=>secs.reduce((a,s)=>{ const x=cm(lc(p,s),la(p,s),m); a.units+=x.units;a.cost+=x.cost;a.sales+=x.sales;a.rev+=x.rev; return a; },{units:0,cost:0,sales:0,rev:0});
  const lineTotal=(cat,acct)=>MONTHS.reduce((a,_,i)=>{ const x=cm(cat,acct,i+1); a.units+=x.units;a.cost+=x.cost;a.sales+=x.sales;a.rev+=x.rev; return a; },{units:0,cost:0,sales:0,rev:0});
  const grandMonth=(m)=>ls.reduce((a,l)=>{ const x=cm(l.category_id,l.account_id,m); a.cost+=x.cost;a.sales+=x.sales;a.rev+=x.rev; return a; },{cost:0,sales:0,rev:0});
  const Money3=({cost,sales,rev,bold})=>(cost||sales||rev)?(
    <div style={{lineHeight:1.4}}>
      {cols.cost && <div style={{color:C.amber}}>{num(cost)}</div>}
      {cols.sales && <div style={{color:C.blue}}>{num(sales)}</div>}
      {cols.revenue && <div style={{color:C.green,fontWeight:bold?800:700}}>{num(rev)}</div>}
    </div>):<span style={{color:'var(--muted)'}}>–</span>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ fontSize:12, color:'var(--muted)' }}>
        Live from <strong>confirmed &amp; approved service orders</strong> at the <strong>actual price sold</strong> (after any per-line discount or increase) — recurring capacity spread across its service months, one-time items in their recognition month. Compare what was actually ordered against the Baseline and Rolling plans.
      </div>
      <div className="card" style={{ padding:0, overflowX:'auto' }}>
        {ls.length===0 ? <Empty>No approved service orders for this year yet.</Empty> : (
          <table style={{ borderCollapse:'collapse', width:'100%', fontSize:12, minWidth:1340, tableLayout:'fixed' }}>
            <thead><tr style={{ background:'var(--surface2)' }}>
              <th style={{ ...thL, width:200 }}>{groupBy==='category'?'Product / Customer':'Customer / Product'}</th>
              {MONTHS.map(m=><th key={m} style={{ ...thC, width:90 }}>{m}</th>)}
              <th style={{ ...thR, width:130 }}>Year Total</th>
            </tr></thead>
            <tbody>
              {Object.entries(groups).map(([pid,secs])=>{
                const pName=groupBy==='category'?catName(pid):acctName(pid);
                const gT=MONTHS.reduce((a,_,i)=>{ const x=monthMoney(secs,pid,i+1); a.units+=x.units;a.cost+=x.cost;a.sales+=x.sales;a.rev+=x.rev; return a; },{units:0,cost:0,sales:0,rev:0});
                const showU = groupBy==='category' && cols.units;
                return (
                  <React.Fragment key={pid}>
                    <tr style={{ background:'var(--surface2)', borderTop:'2px solid var(--border)' }}>
                      <td style={{ ...tdL, fontWeight:800, background:'var(--surface2)' }}>{pName}</td>
                      {MONTHS.map((_,i)=>{ const mm=monthMoney(secs,pid,i+1); return (
                        <td key={i} style={tdC10}>{(mm.units||mm.cost||mm.sales||mm.rev)?(
                          <div style={{lineHeight:1.4}}>
                            {showU && <div style={{fontWeight:800}}>{num(mm.units)}</div>}
                            {cols.cost && <div style={{color:C.amber}}>{num(mm.cost)}</div>}
                            {cols.sales && <div style={{color:C.blue}}>{num(mm.sales)}</div>}
                            {cols.revenue && <div style={{color:C.green,fontWeight:800}}>{num(mm.rev)}</div>}
                          </div>
                        ):<span style={{color:'var(--muted)'}}>–</span>}</td>
                      ); })}
                      <td style={{ ...tdR, fontSize:10, lineHeight:1.5 }}>
                        {showU && <div style={{ fontWeight:800 }}>{uni(gT.units)}</div>}
                        {cols.cost && <div style={{ color:C.amber }}>{num(gT.cost)}</div>}
                        {cols.sales && <div style={{ color:C.blue }}>{num(gT.sales)}</div>}
                        {cols.revenue && <div style={{ color:C.green, fontWeight:800 }}>{num(gT.rev)}</div>}
                      </td>
                    </tr>
                    {secs.map(s=>{ const cat=lc(pid,s),acct=la(pid,s); const sName=groupBy==='category'?acctName(s):catName(s); const lt=lineTotal(cat,acct);
                      return (
                        <tr key={s} style={{ borderTop:'1px solid var(--border)' }}>
                          <td style={{ ...tdL, paddingLeft:26, color:'var(--muted2)' }}>{sName}</td>
                          {MONTHS.map((_,i)=>{ const x=cm(cat,acct,i+1); return (
                            <td key={i} style={tdC10}>{x.units>0
                              ? <div style={{ lineHeight:1.4 }}>
                                  {cols.units && <div style={{ fontWeight:700 }}>{num(x.units)}</div>}
                                  {cols.cost && <div style={{ color:C.amber }}>{num(x.cost)}</div>}
                                  {cols.sales && <div style={{ color:C.blue }}>{num(x.sales)}</div>}
                                  {cols.revenue && <div style={{ color:C.green, fontWeight:700 }}>{num(x.rev)}</div>}
                                </div>
                              : <span style={{ color:'var(--muted)' }}>–</span>}</td>
                          ); })}
                          <td style={{ ...tdR, fontSize:10, lineHeight:1.5 }}>
                            {cols.units && <div style={{ fontWeight:700 }}>{uni(lt.units)}</div>}
                            {cols.revenue && <div style={{ color:C.green }}>{mny(lt.rev, curOf(cat))}</div>}
                          </td>
                        </tr>
                      ); })}
                  </React.Fragment>
                );
              })}
              <tr style={{ borderTop:'2px solid var(--border)', background:'var(--surface2)' }}>
                <td style={{ ...tdL, fontWeight:800, background:'var(--surface2)' }}>Grand Total</td>
                {MONTHS.map((_,i)=><td key={i} style={tdC10}><Money3 {...grandMonth(i+1)} bold/></td>)}
                <td style={{ ...tdR, fontSize:10, lineHeight:1.5 }}>
                  {(()=>{ const g=MONTHS.reduce((a,_,i)=>{ const x=grandMonth(i+1); a.cost+=x.cost;a.sales+=x.sales;a.rev+=x.rev; return a; },{cost:0,sales:0,rev:0}); return (
                    <>{cols.cost && <div style={{ color:C.amber }}>{num(g.cost)}</div>}{cols.sales && <div style={{ color:C.blue }}>{num(g.sales)}</div>}{cols.revenue && <div style={{ color:C.green, fontWeight:800 }}>{num(g.rev)}</div>}</>
                  ); })()}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ─── Actual Sales grid ─────────────────────────────────────────────────── */
function ActualGrid({ A, groupBy, fCustomer, fProduct, catName, acctName, priceOf, cols={units:true,cost:true,sales:true,revenue:true} }) {
  if (!A) return <Empty>Loading actual sales…</Empty>;
  const frozen = new Set(A.frozen_months||[]);
  const k=(c,a,m)=>`${c}|${a||''}|${m}`;
  const aC={}, bC={}, set=[];
  (A.baseline_values||[]).forEach(v=>{ bC[k(v.category_id,v.account_id,v.month)]=v; push(v); });
  (A.actual_values||[]).forEach(v=>{ aC[k(v.category_id,v.account_id,v.month)]=v; push(v); });
  function push(v){ if(!set.find(l=>l.category_id===v.category_id&&l.account_id===(v.account_id||''))) set.push({category_id:v.category_id,account_id:v.account_id||''}); }
  const pr=id=>(A.pricing&&A.pricing[id])||priceOf(id)||{price:0,cost:0,margin:0};
  const met=(cell)=>{ if(!cell) return {units:0,cost:0,sales:0,rev:0}; const u=Number(cell.amount)||0; if(cell.frozen&&cell.snap_revenue!=null) return {units:u,cost:r3(u*Number(cell.snap_cost||0)),sales:r3(u*Number(cell.snap_price||0)),rev:Number(cell.snap_revenue)}; const p=pr(cell.category_id); return {units:u,cost:r3(u*p.cost),sales:r3(u*p.price),rev:r3(u*(p.margin!=null?p.margin:p.price-p.cost))}; };
  const aM=(c,a,m)=>met(aC[k(c,a,m)]), bM=(c,a,m)=>met(bC[k(c,a,m)]);
  const ls = set.filter(l=>(!fCustomer||l.account_id===fCustomer)&&(!fProduct||l.category_id===fProduct));
  const groups={}; ls.forEach(l=>{ const p=groupBy==='category'?l.category_id:(l.account_id||''); const s=groupBy==='category'?(l.account_id||''):l.category_id; groups[p]=groups[p]||[]; if(!groups[p].includes(s)) groups[p].push(s); });
  const lc=(p,s)=>groupBy==='category'?p:s, la=(p,s)=>groupBy==='category'?s:p;
  const sum=(fn,secs,p,m)=>secs.reduce((a,s)=>{const x=fn(lc(p,s),la(p,s),m); a.units+=x.units;a.cost+=x.cost;a.sales+=x.sales;a.rev+=x.rev; return a;},{units:0,cost:0,sales:0,rev:0});
  const gm=(fn,i)=>ls.reduce((a,l)=>{const x=fn(l.category_id,l.account_id,i+1); a.units+=x.units;a.cost+=x.cost;a.sales+=x.sales;a.rev+=x.rev; return a;},{units:0,cost:0,sales:0,rev:0});

  const VL=({label,a,b,color})=>{ const d=r3(a-b),p=pct(a,b),vc=d>0?C.green:d<0?C.red:'var(--muted)';
    return <div title={`${label}: actual ${num(a)} vs baseline ${num(b)} → ${d>=0?'+':''}${num(d)} (${d>=0?'+':''}${p}%)`}><span style={{fontWeight:700,color}}>{label} {num(a)}</span> <span style={{fontSize:8.5,color:vc}}>{d>=0?'+':''}{p}%</span></div>; };
  const Cell=({ma,mb,show})=>{ if(!show) return <span style={{color:'var(--muted)'}}>–</span>; const du=r3(ma.units-mb.units);
    return <div style={{lineHeight:1.35}}>
      {cols.units && <div style={{fontWeight:800}} title={`Actual ${uni(ma.units)} vs baseline ${uni(mb.units)}`}>{uni(ma.units)} <span style={{fontSize:8.5,color:du>0?C.green:du<0?C.red:'var(--muted)'}}>{du>=0?'+':''}{num(du)}</span></div>}
      {cols.cost && <VL label="C" a={ma.cost} b={mb.cost} color={C.amber}/>}
      {cols.sales && <VL label="S" a={ma.sales} b={mb.sales} color={C.blue}/>}
      {cols.revenue && <VL label="R" a={ma.rev} b={mb.rev} color={C.green}/>}
    </div>; };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ fontSize:12, color:'var(--muted)' }}>
        Comparing against <strong>{A.baseline_label||'Baseline'}</strong>. {frozen.size===0?'No months frozen yet — freeze a Rolling month to populate.':`Frozen: ${[...frozen].sort((a,b)=>a-b).map(m=>MONTHS[m-1]).join(', ')}.`} Each frozen cell shows units &amp; <span style={{color:C.amber}}>cost</span> / <span style={{color:C.blue}}>sales</span> / <span style={{color:C.green}}>revenue</span> with variance vs baseline.
      </div>
      <div className="card" style={{ padding:0, overflowX:'auto' }}>
        {ls.length===0 ? <Empty>Approve a Baseline first; the actual grid mirrors its lines.</Empty> : (
          <table style={{ borderCollapse:'collapse', width:'100%', fontSize:12, minWidth:1560, tableLayout:'fixed' }}>
            <thead><tr style={{ background:'var(--surface2)' }}>
              <th style={{ ...thL, width:180 }}>{groupBy==='category'?'Product / Customer':'Customer / Product'}</th>
              {MONTHS.map((m,i)=><th key={m} style={{ ...thC, width:112, color:frozen.has(i+1)?C.green:'var(--muted)' }}>{m}{frozen.has(i+1)?' 🔒':''}</th>)}
              <th style={{ ...thR, width:120 }}>YTD Actual</th>
            </tr></thead>
            <tbody>
              {Object.entries(groups).map(([pid,secs])=>{
                const pName=groupBy==='category'?catName(pid):acctName(pid);
                const ytd=r3(MONTHS.reduce((s,_,i)=>s+(frozen.has(i+1)?sum(aM,secs,pid,i+1).rev:0),0));
                return (
                  <React.Fragment key={pid}>
                    <tr style={{ background:'var(--surface2)', borderTop:'2px solid var(--border)' }}>
                      <td style={{ ...tdL, fontWeight:800, background:'var(--surface2)' }}>{pName}</td>
                      {MONTHS.map((_,i)=><td key={i} style={tdC10}><Cell ma={sum(aM,secs,pid,i+1)} mb={sum(bM,secs,pid,i+1)} show={frozen.has(i+1)}/></td>)}
                      <td style={{ ...tdR, fontWeight:800, color:C.green }}>{num(ytd)}</td>
                    </tr>
                    {secs.map(s=>{ const cat=lc(pid,s),acct=la(pid,s); const sName=groupBy==='category'?acctName(s):catName(s);
                      const y2=r3(MONTHS.reduce((x,_,i)=>x+(frozen.has(i+1)?aM(cat,acct,i+1).rev:0),0));
                      return (
                        <tr key={s} style={{ borderTop:'1px solid var(--border)' }}>
                          <td style={{ ...tdL, paddingLeft:26, color:'var(--muted2)' }}>{sName}</td>
                          {MONTHS.map((_,i)=><td key={i} style={tdC10}><Cell ma={aM(cat,acct,i+1)} mb={bM(cat,acct,i+1)} show={frozen.has(i+1)}/></td>)}
                          <td style={{ ...tdR, fontWeight:700, color:C.green }}>{num(y2)}</td>
                        </tr>
                      ); })}
                  </React.Fragment>
                );
              })}
              <tr style={{ borderTop:'2px solid var(--border)', background:'var(--surface2)' }}>
                <td style={{ ...tdL, fontWeight:800, background:'var(--surface2)' }}>Grand Total</td>
                {MONTHS.map((_,i)=><td key={i} style={tdC10}><Cell ma={gm(aM,i)} mb={gm(bM,i)} show={frozen.has(i+1)}/></td>)}
                <td style={{ ...tdR, fontWeight:800, color:C.green }}>{num(r3(MONTHS.reduce((s,_,i)=>s+(frozen.has(i+1)?gm(aM,i).rev:0),0)))}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ─── Dialogs ────────────────────────────────────────────────────────────── */
function Dialogs({ dlg, onClose, users, year, editing, onNewDraft, onDeleteForecast, onResetYear, onSubmitBaseline, onSubmitMonth, busy }) {
  const [val, setVal] = useState('');
  const [appr, setAppr] = useState('');
  if (dlg.kind==='newDraft') return (
    <Modal title="New baseline draft" subtitle="Give this scenario a short name or code." onClose={onClose} width={420}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={()=>onNewDraft(val.trim()||`Draft ${year}`)}>Create</button></>}>
      <input className="input" placeholder="e.g. Conservative, Stretch 2026" value={val} onChange={e=>setVal(e.target.value)} autoFocus />
    </Modal>
  );
  if (dlg.kind==='submitBaseline') return (
    <Modal title="Submit baseline for approval" subtitle="Once approved, the baseline is frozen for the year." onClose={onClose} width={440}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!appr||busy} onClick={()=>onSubmitBaseline(appr)}>Submit</button></>}>
      <Field label="Approver"><select className="select" style={{ width:'100%' }} value={appr} onChange={e=>setAppr(e.target.value)}><option value="">Select approver…</option>{users.map(u=><option key={u.id} value={u.id}>{u.full_name}</option>)}</select></Field>
    </Modal>
  );
  if (dlg.kind==='freezeMonth') return (
    <Modal title={`Freeze ${MONTHS[dlg.month-1]} ${year}`} subtitle="Choose who approves the freeze. Once approved this month's values are locked permanently." onClose={onClose} width={440}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!appr||busy} onClick={()=>onSubmitMonth(dlg.month,appr)}>Submit</button></>}>
      <Field label="Approver"><select className="select" style={{ width:'100%' }} value={appr} onChange={e=>setAppr(e.target.value)}><option value="">Select approver…</option>{users.map(u=><option key={u.id} value={u.id}>{u.full_name}</option>)}</select></Field>
    </Modal>
  );
  if (dlg.kind==='deleteForecast') return (
    <Modal title={`Delete this ${dlg.forecast.status==='approved'?'baseline':'draft'}?`} danger onClose={onClose} width={420}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn" style={{ background:C.red, color:'#fff', borderColor:C.red }} disabled={busy} onClick={()=>onDeleteForecast(dlg.forecast)}>Delete</button></>}>
      <p style={{ fontSize:13, color:'var(--muted2)', lineHeight:1.6 }}><strong>{dlg.forecast.name||(dlg.forecast.status==='approved'?'Approved baseline':'This draft')}</strong> and all its figures will be permanently removed. This cannot be undone.</p>
    </Modal>
  );
  if (dlg.kind==='resetYear') return (
    <Modal title={`Reset all ${year} forecast data?`} danger onClose={onClose} width={460}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn" style={{ background:C.red, color:'#fff', borderColor:C.red }} disabled={busy} onClick={onResetYear}>Reset everything</button></>}>
      <p style={{ fontSize:13, color:'var(--muted2)', lineHeight:1.6 }}>This permanently deletes <strong>every</strong> baseline (including the approved one), the rolling forecast, all monthly freezes, and the actual sales grid for {year}. This cannot be undone.</p>
    </Modal>
  );
  return null;
}

/* ─── shared styles + atoms ─────────────────────────────────────────────── */
const Empty = ({ children }) => <div style={{ padding:'40px 24px', textAlign:'center', color:'var(--muted)', fontSize:13 }}>{children}</div>;
const thBase = { padding:'11px 8px', fontSize:11, textTransform:'uppercase', letterSpacing:'.4px', color:'var(--muted)', fontWeight:700 };
const thL = { ...thBase, textAlign:'left', position:'sticky', left:0, background:'var(--surface2)' };
const thC = { ...thBase, textAlign:'center', padding:'11px 4px' };
const thR = { ...thBase, textAlign:'right', padding:'11px 12px' };
const tdL = { padding:'8px 14px', position:'sticky', left:0, background:'var(--surface)', whiteSpace:'nowrap' };
const tdC10 = { padding:'6px 4px', textAlign:'center', fontSize:10 };
const tdR = { padding:'8px 12px', textAlign:'right' };
